import {getTestProps} from '@benchling/js-test-utils';
import {Box, Checkbox, createStyles, Modal} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React, {useCallback, useMemo, useRef, useState} from 'react';

import {makeAnalyticsEventId, trackEventWithModule} from 'analytics/Analytics';
import {UPDATE_FLOWCHART_MODAL_TYPE} from 'constants/analytics-products/WorkflowAnalyticsConstants';
import {
  ActionMethod,
  ActionOrigin,
  ANALYTICS_MODULE,
  CloseType,
  COMMON_ANALYTICS_ACTIONS,
  WORKFLOWS_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {isNodeTesting} from 'lib/Browser';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import preferences from 'Preferences';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import ImprovedProcessUpdateFlowchartModal from 'processes/modals/ImprovedProcessUpdateFlowchartModal';
import collapseEmptyExecutionNodes from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/execution/collapseEmptyExecutionNodes';
import ExecutionFlowchartPane from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/execution/ExecutionFlowchartPane';
import {useExecutionFlowchartDraftStateForProcess} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/execution/useExecutionFlowchartDraftState';
import {FlowchartConfigWithMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/flowchartConfigAutosaveManagerCallbacks';
import {PROCESS_FLOWCHART_CONFIG_EDITOR_QUERY} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useProcessFlowchartConfigQuery';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {warn} from 'util/Logging';

import MappingInProgressIndicator from './MappingInProgressIndicator';
import ReadOnlyFlowchart from './ReadOnlyFlowchart';
import type {ViewerStaticTemplateFlowchart} from './ReadOnlyFlowchartTypes';

const SHOW_NODES_WITHOUT_TASKS_PREF_KEY = 'PROCESSES_SHOW_NODES_WITHOUT_TASKS_IN_FLOWCHART_VISUALIZATION';

const HEADER_Z = 5;

const useTaskGroupFlowchartExecutionViewerStyles = createStyles((theme) => ({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    flexShrink: 0,
    width: '100%',
    zIndex: HEADER_Z,
    backgroundColor: theme.white,
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 48,
  },
  heading: {
    marginLeft: theme.spacing.md,
    color: theme.colors.gray[7],
  },
  toolbar: {
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    columnGap: theme.spacing.xs,
    marginRight: theme.spacing.md,
  },
  editButtonActive: {
    '&:hover': {
      color: theme.colors.blue[4],
    },
  },
  pane: {
    flex: 1,
    minHeight: 0,
    position: 'relative',
  },
  mappingInProgressIndicator: {
    position: 'absolute',
    top: theme.spacing.xs,
    marginLeft: theme.spacing.md,
    zIndex: HEADER_Z,
  },
}));

export interface TaskGroupFlowchartExecutionViewerProps {
  flowchartProcessId: string;
  flowchartTitle: string;
  rootProcessTaskSchemaId: string;
  sourceType: string;
  editDisabledMessage: string | null;
  canEditFlowchart: boolean;
  onFlowchartModified: () => void;
  isFlowchartMappingInProgress?: boolean;
  actionOrigin: ActionOrigin;
  onSetProcess?: (processId: string) => void;
  activeProcessId?: string;
  lineageRootTaskId?: string;
  displayOnlyFlowchart?: ViewerStaticTemplateFlowchart;
  displayOnlyProcessIdsByNodeConfigId?: Record<string, string>;
  lineageEdgeHashes?: ReadonlySet<string>;
  /** Overrides node URL map derived from the execution draft hook. Used when the
   *  caller already has URL data from its own GQL query (e.g. task-details page). */
  nodeUrlsByNodeConfigId?: Record<string, string>;
}

/**
 * Execution flowchart for a task-group root process: header, optional "update flowchart"
 * modal, and {@link ExecutionFlowchartPane}. Call `onFlowchartModified` after saves so lineage
 * data refetches (see `ProcessTaskGroupLineageViewer`).
 */
export default function TaskGroupFlowchartExecutionViewer(
  props: TaskGroupFlowchartExecutionViewerProps
): JSX.Element {
  const [pcmEditorOpened, {open: openPcmEditor, close: closePcmEditor}] = useDisclosure(false);
  const [showNodesWithoutTasks, setShowNodesWithoutTasks] = useState(
    () => preferences.get(SHOW_NODES_WITHOUT_TASKS_PREF_KEY) ?? true
  );

  const {classes, cx} = useTaskGroupFlowchartExecutionViewerStyles();
  const {
    draftState,
    setDraftState,
    clearDraft,
    readinessSnapshot,
    processIdsByNodeConfigId,
    nodeUrlsByNodeConfigId,
  } = useExecutionFlowchartDraftStateForProcess({
    processId: props.flowchartProcessId,
    editorMode: pcmEditorOpened ? FlowchartEditorMode.PCM_EDITOR : FlowchartEditorMode.EXECUTION_VIEWER,
    lineageRootTaskId: props.lineageRootTaskId,
    skip: props.displayOnlyFlowchart != null && !pcmEditorOpened,
  });

  const {onSetProcess, activeProcessId} = props;
  const effectiveProcessIdsByNodeConfigId =
    props.displayOnlyProcessIdsByNodeConfigId ?? processIdsByNodeConfigId;
  const effectiveProcessIdsRef = useRef(effectiveProcessIdsByNodeConfigId);
  effectiveProcessIdsRef.current = effectiveProcessIdsByNodeConfigId;
  const onSetProcessRef = useRef(onSetProcess);
  onSetProcessRef.current = onSetProcess;
  const effectiveNodeUrlsByNodeConfigId = props.nodeUrlsByNodeConfigId ?? nodeUrlsByNodeConfigId;
  const nodeUrlsByNodeConfigIdRef = useRef(effectiveNodeUrlsByNodeConfigId);
  nodeUrlsByNodeConfigIdRef.current = effectiveNodeUrlsByNodeConfigId;
  const hasNodeUrls =
    effectiveNodeUrlsByNodeConfigId != null && Object.keys(effectiveNodeUrlsByNodeConfigId).length > 0;
  const onNodeNavigateClick = useMemo(() => {
    // Task groups: navigate to a child process via onSetProcess (inline panel navigation).
    if (onSetProcess != null) {
      return (nodeConfigId: string) => {
        const processId = effectiveProcessIdsRef.current?.[nodeConfigId];
        if (processId != null) {
          onSetProcessRef.current?.(processId);
        }
      };
    }
    // Procedure run tasks: navigate to the task URL (full page navigation) when no
    // process ID mapping is available.
    if (hasNodeUrls) {
      return (nodeConfigId: string) => {
        const url = nodeUrlsByNodeConfigIdRef.current?.[nodeConfigId];
        if (url != null) {
          navigateToURL(url);
        }
      };
    }
    return undefined;
  }, [onSetProcess, hasNodeUrls]);

  const navigableNodeConfigIds = useMemo((): ReadonlySet<string> | undefined => {
    if (onSetProcess != null && effectiveProcessIdsByNodeConfigId != null) {
      return new Set(Object.keys(effectiveProcessIdsByNodeConfigId));
    }
    if (hasNodeUrls && nodeUrlsByNodeConfigId != null) {
      return new Set(Object.keys(nodeUrlsByNodeConfigId));
    }
    return undefined;
  }, [onSetProcess, effectiveProcessIdsByNodeConfigId, hasNodeUrls, nodeUrlsByNodeConfigId]);

  const selectedNodeConfigId = useMemo(() => {
    if (activeProcessId == null || effectiveProcessIdsByNodeConfigId == null) {
      return undefined;
    }
    return Object.entries(effectiveProcessIdsByNodeConfigId).find(
      ([, processId]) => processId === activeProcessId
    )?.[0];
  }, [activeProcessId, effectiveProcessIdsByNodeConfigId]);

  const filteredDisplayOnlyFlowchart = useMemo((): ViewerStaticTemplateFlowchart | undefined => {
    if (props.displayOnlyFlowchart == null || showNodesWithoutTasks) {
      return props.displayOnlyFlowchart;
    }
    try {
      return collapseEmptyExecutionNodes(props.displayOnlyFlowchart, props.lineageEdgeHashes);
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      warn('Unable to collapse nodes in flowchart', {extra: {error: e}});
      return props.displayOnlyFlowchart;
    }
  }, [props.displayOnlyFlowchart, props.lineageEdgeHashes, showNodesWithoutTasks]);

  const flowchartStructureKey = useMemo(
    () => filteredDisplayOnlyFlowchart?.nodesStaticData.map((n) => n.id).join(',') ?? '',
    [filteredDisplayOnlyFlowchart]
  );

  const [pcmEditorEventGroupId, setPcmEditorEventGroupId] = useState('');
  // Snapshot of the draft taken when the PCM editor opens. The editor mutates the shared draft
  // as the user edits, so on Cancel/Exit we restore this snapshot to keep the inline execution
  // viewer from picking up unsaved changes (mirrors the ACM `lastSavedDraftState` pattern in
  // `CreateProcessModal.useCreateProcessFlowchartPaneState`).
  const [preEditDraftSnapshot, setPreEditDraftSnapshot] = useState<FlowchartConfigWithMetadata | null>(null);

  const revertDraftAndClose = useCallback((): void => {
    setDraftState(preEditDraftSnapshot);
    closePcmEditor();
  }, [closePcmEditor, preEditDraftSnapshot, setDraftState]);

  const handlePcmEditorExit = useCallback(() => {
    if (pcmEditorEventGroupId !== '') {
      trackEventWithModule(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
        event_group_id: pcmEditorEventGroupId,
        action_origin: props.actionOrigin,
        action_method: ActionMethod.POST_CREATION_FLOWCHART_MODIFICATION,
        close_type: CloseType.EXIT,
        modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
      });
    }
    revertDraftAndClose();
  }, [pcmEditorEventGroupId, props.actionOrigin, revertDraftAndClose]);

  const handlePcmEditorSubmit = useCallback(async (): Promise<void> => {
    closePcmEditor();
    // Refetch the flowchart config and then clear the draft so it rebuilds the draft from the new
    // data
    try {
      await getApolloClient().query({
        query: PROCESS_FLOWCHART_CONFIG_EDITOR_QUERY,
        variables: {flowchartConfigId: readinessSnapshot.data!.flowchartConfigId},
        fetchPolicy: 'network-only',
      });
    } catch (err) {
      notifications.error(
        'Flowchart saved but failed to refresh. Please reload the page to see the latest flowchart.'
      );
      throw err;
    } finally {
      clearDraft();
    }
  }, [clearDraft, closePcmEditor, readinessSnapshot.data]);

  return (
    <Box
      className={classes.root}
      {...getTestProps({component: 'TaskGroupFlowchartExecutionViewer', element: 'root'})}
    >
      <Box className={classes.header}>
        <Box className={classes.heading}>
          <BenchlingIcon icon="workflow" /> &nbsp;
          {props.flowchartTitle}
        </Box>
        <Box className={classes.toolbar}>
          <Checkbox
            label="Show nodes without tasks"
            checked={showNodesWithoutTasks}
            onChange={(e) => {
              const checked = e.target.checked;
              preferences.set(SHOW_NODES_WITHOUT_TASKS_PREF_KEY, checked);
              setShowNodesWithoutTasks(checked);
              const eventGroupId = makeAnalyticsEventId();
              trackEventWithModule(
                WORKFLOWS_ANALYTICS_ACTIONS.TOGGLED_SHOW_NODES_WITHOUT_DATA_IN_FLOWCHART_LINEAGE,
                ANALYTICS_MODULE.WORKFLOWS,
                {
                  event_group_id: eventGroupId,
                  action_origin: props.actionOrigin,
                  action_method: ActionMethod.BUTTON_CLICK,
                  show_nodes_without_tasks: checked,
                }
              );
            }}
            {...getTestProps({
              component: 'TaskGroupFlowchartExecutionViewer',
              element: 'showNodesWithoutTasksCheckbox',
            })}
          />
          {props.canEditFlowchart && (
            <TooltipWrapper tooltipTitle={props.editDisabledMessage}>
              <Button
                buttonStyle={ButtonStyle.TERTIARY_WHITE}
                size={ButtonSize.MINI}
                icon="edit"
                text="Edit"
                className={cx(props.editDisabledMessage == null && classes.editButtonActive)}
                disabled={props.editDisabledMessage != null}
                onClick={() => {
                  const eventGroupId = makeAnalyticsEventId();
                  setPcmEditorEventGroupId(eventGroupId);
                  trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
                    event_group_id: eventGroupId,
                    action_origin: props.actionOrigin,
                    action_method: ActionMethod.POST_CREATION_FLOWCHART_MODIFICATION,
                    task_schema_api_id: props.rootProcessTaskSchemaId,
                    modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
                    process_id: props.flowchartProcessId,
                  });
                  setPreEditDraftSnapshot(draftState);
                  openPcmEditor();
                }}
                dataTestAttrs={{
                  component: 'TaskGroupFlowchartExecutionViewer',
                  element: 'openProcessUpdateFlowchartModal',
                }}
              />
            </TooltipWrapper>
          )}
        </Box>
      </Box>
      <Box className={classes.pane}>
        {props.isFlowchartMappingInProgress && (
          <Box className={classes.mappingInProgressIndicator}>
            <MappingInProgressIndicator
              text="Mapping in progress..."
              dataTestAttrs={{
                component: 'TaskGroupFlowchartExecutionViewer',
                element: 'mappingInProgressIndicator',
              }}
            />
          </Box>
        )}
        {!pcmEditorOpened && filteredDisplayOnlyFlowchart != null && (
          <ReadOnlyFlowchart
            key={`${showNodesWithoutTasks}-${flowchartStructureKey}`}
            flowchart={filteredDisplayOnlyFlowchart}
            rootTaskSchemaId={props.rootProcessTaskSchemaId}
            onNodeNavigateClick={onNodeNavigateClick}
            selectedNodeConfigId={selectedNodeConfigId}
          />
        )}
        {!pcmEditorOpened && filteredDisplayOnlyFlowchart == null && props.displayOnlyFlowchart == null && (
          <ExecutionFlowchartPane
            key={`${showNodesWithoutTasks}-${flowchartStructureKey}`}
            editorMode={FlowchartEditorMode.EXECUTION_VIEWER}
            processId={props.flowchartProcessId}
            draftState={draftState}
            setDraftState={setDraftState}
            readinessSnapshot={readinessSnapshot}
            onNodeNavigateClick={onNodeNavigateClick}
            navigableNodeConfigIds={navigableNodeConfigIds}
            selectedNodeConfigId={selectedNodeConfigId}
          />
        )}
      </Box>
      <Modal
        opened={pcmEditorOpened}
        onClose={handlePcmEditorExit}
        title="Update flowchart"
        size="98%"
        centered={true}
        closeOnEscape={false}
        padding={0}
        withinPortal={!isNodeTesting}
        styles={{
          content: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '100%',
            height: '90vh',
            maxHeight: '90vh',
          },
          inner: {padding: '0 !important'},
          body: {
            padding: '0 !important',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          },
        }}
      >
        {pcmEditorOpened ? (
          <ImprovedProcessUpdateFlowchartModal
            flowchartProcessId={props.flowchartProcessId}
            sourceType={props.sourceType}
            onFlowchartModified={props.onFlowchartModified}
            eventGroupId={pcmEditorEventGroupId}
            actionOrigin={props.actionOrigin}
            draftState={draftState}
            setDraftState={setDraftState}
            readinessSnapshot={readinessSnapshot}
            initialDraftState={preEditDraftSnapshot}
            onCancel={revertDraftAndClose}
            onSubmit={handlePcmEditorSubmit}
          />
        ) : null}
      </Modal>
    </Box>
  );
}
